import { Component, ViewChild } from '@angular/core'
import { AuthService, KEY_TOKEN } from '@tv3/services/auth.service'
import { UtilityService } from '@tv3/services/utils/utility.service'
import { environment } from '@tv3/environments/environment'
import { Destroyable, isSomething, LocalStorage, untilDestroy } from '@tokeet-frontend/tv3-platform'
import * as R from 'ramda'
import { select, Store } from '@ngrx/store'
import { OpenInventoryOverlay } from '@tv3/store/overlay/overlay.actions'
import { selectActiveProductPlan, selectActiveProductPlanView } from '@tv3/store/plan/plan.selectors'
import { isSubscriptionValid, ProductsForPlan } from '@tv3/store/plan/plan.model'
import { map } from 'rxjs/operators'
import { userRoleToFriendlyString } from '@tv3/utils/functions/user-role-to-string'
import { BsDropdownDirective } from 'ngx-bootstrap/dropdown'
import { AmplitudeService } from '@tv3/services/amplitude.service'
import { AIResponderSettingsService } from '@tv3/containers/ai-responder/settings/ai-responser-settings.service'
import { PushRatesOverlayService } from '@tv3/containers/rates/rate-push-automations/push-rates-overlay/push-rates-overlay.service'
import { CostEstimatorDialogService } from '@tv3/containers/rates/cost-estimator-dialog/cost-estimator-dialog.service'
import { MessagingSettingsDialogService } from '@tv3/containers/messaging-settings/settings/messaging-settings.service'
import { AppOverviewDialogService } from '@tokeet-frontend/billing'
import { AIChatOverlayService } from '../ai-chat-overlay/ai-chat-overlay.service'

declare const window: any

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
})
export class NavbarComponent extends Destroyable {
  @ViewChild('websiteWidgetMenu', { static: false }) websiteWidgetMenu: BsDropdownDirective

  isNavBarCollapsed = false
  isSettingsOpen = false
  isIntegrationsOpen = false
  showCompliance = environment.features.compliance

  isFreePlan$ = this.store.pipe(
    select(selectActiveProductPlanView(ProductsForPlan.AdvanceCM)),
    map((plan) => !plan || plan?.isFree)
  )

  isWebreadyInactive$ = this.store.pipe(
    select(selectActiveProductPlan, { product: ProductsForPlan.Webready }),
    map((plan) => !isSubscriptionValid(plan)),
    untilDestroy(this)
  )

  isPowerReportingInactive$ = this.store.pipe(
    select(selectActiveProductPlan, { product: ProductsForPlan.PowerReporting }),
    map((plan) => !isSubscriptionValid(plan)),
    untilDestroy(this)
  )

  isRateGenieInactive$ = this.store.pipe(
    select(selectActiveProductPlan, { product: ProductsForPlan.Rategenie }),
    map((plan) => !isSubscriptionValid(plan)),
    untilDestroy(this)
  )

  isAutomataInactive$ = this.store.pipe(
    select(selectActiveProductPlan, { product: ProductsForPlan.Automata }),
    map((plan) => !isSubscriptionValid(plan)),
    untilDestroy(this)
  )

  isSignatureInactive$ = this.store.pipe(
    select(selectActiveProductPlan, { product: ProductsForPlan.Signature }),
    map((plan) => !isSubscriptionValid(plan)),
    untilDestroy(this)
  )

  isChecklistInactive$ = this.store.pipe(
    select(selectActiveProductPlan, { product: ProductsForPlan.Checklist }),
    map((plan) => !isSubscriptionValid(plan)),
    untilDestroy(this)
  )

  aiResponderSub$ = this.store.pipe(
    select(selectActiveProductPlanView(ProductsForPlan.TokeetAI)),
    map((res) => res || {})
  )

  isWebreadyInactiveTooltip$ = this.isWebreadyInactive$.pipe(
    map((inactive) => {
      return inactive ? $localize`Go to app store to update subscription for Webready` : ``
    })
  )
  isPowerReportingInactiveTooltip$ = this.isPowerReportingInactive$.pipe(
    map((inactive) => {
      return inactive ? $localize`Go to app store to update subscription for Power Reporting` : ``
    })
  )
  isRateGenieInactiveTooltip$ = this.isRateGenieInactive$.pipe(
    map((inactive) => {
      return inactive ? $localize`Go to app store to update subscription for Rategenie` : ``
    })
  )
  isAutomataInactiveTooltip$ = this.isAutomataInactive$.pipe(
    map((inactive) => {
      return inactive ? $localize`Go to app store to update subscription for Automata` : ``
    })
  )
  isSignatureInactiveTooltip$ = this.isSignatureInactive$.pipe(
    map((inactive) => {
      return inactive ? $localize`Go to app store to update subscription for Signature` : ``
    })
  )
  isChecklistInactiveTooltip$ = this.isChecklistInactive$.pipe(
    map((inactive) => {
      return inactive ? $localize`Go to app store to update subscription for Checklist` : ``
    })
  )

  constructor(
    public utility: UtilityService,
    private storage: LocalStorage,
    private store: Store<any>,
    private auth: AuthService,
    private amplitudeService: AmplitudeService,
    private pushRatesOverlayService: PushRatesOverlayService,
    private aiSettings: AIResponderSettingsService,
    private messaginSettings: MessagingSettingsDialogService,
    private appOverviewDialog: AppOverviewDialogService,
    private aiChatOverlay: AIChatOverlayService,
    private costEstimator: CostEstimatorDialogService
  ) {
    super()
  }

  get name() {
    if (this.auth.user) {
      const firstName = this.auth.user.firstName
      const lastName = this.auth.user.lastName
      return `${firstName} ${lastName}`
    } else {
      return ''
    }
  }

  get role() {
    if (this.auth.user) {
      return userRoleToFriendlyString(this.auth.user.roles)
    } else {
      return ''
    }
  }

  dropdownOverlayAttached() {
    // add class "xx" to parent ".cdk-overlay-container"
    const parent = document.querySelector('.cdk-overlay-container')
    parent.classList.add('overflow-auto')
  }

  dropdownOverlayDetached() {
    // remove class "xx" from parent ".cdk-overlay-container"
    const parent = document.querySelector('.cdk-overlay-container')
    parent.classList.remove('overflow-auto')
  }

  onOpenInventory() {
    this.store.dispatch(OpenInventoryOverlay())
  }

  canSee(
    product: 'power_reporting' | 'ownercenter' | 'rategenie' | 'automata' | 'signature' | 'checklist' | 'webready'
  ) {
    if (this.auth.hasRole('admin')) {
      return true
    }

    if (product === 'power_reporting' && (this.auth.hasRole('manager') || this.auth.hasRole('accounting'))) {
      return true
    }

    return !!R.pathOr(0, ['settings', product], this.auth.user)
  }

  appStoreUrl() {
    let token = this.auth.token
    if (!isSomething(token)) {
      token = this.storage.get(KEY_TOKEN)
    }
    return `${environment.appStoreUrl}login?token=${token}`
  }

  rategenieUrl() {
    let token = this.auth.token
    if (!isSomething(token)) {
      token = this.storage.get(KEY_TOKEN)
    }
    return `${environment.rategenieUrl}login?token=${token}`
  }

  webreadyUrl() {
    let token = this.auth.token
    if (!isSomething(token)) {
      token = this.storage.get(KEY_TOKEN)
    }
    return `${environment.webreadyUrl}login?token=${token}`
  }

  powerReportingUrl() {
    let token = this.auth.token
    if (!isSomething(token)) {
      token = this.storage.get(KEY_TOKEN)
    }
    return `${environment.powerReportingUrl}/login?token=${token}`
  }

  automataUrl() {
    let token = this.auth.token
    if (!isSomething(token)) {
      token = this.storage.get(KEY_TOKEN)
    }
    return `${environment.automataUrl}login?token=${token}`
  }

  signatureUrl() {
    let token = this.auth.token
    if (!isSomething(token)) {
      token = this.storage.get(KEY_TOKEN)
    }
    return `${environment.signatureUrl}?token=${token}`
  }

  checklistUrl() {
    let token = this.auth.token
    if (!isSomething(token)) {
      token = this.storage.get(KEY_TOKEN)
    }
    return `${environment.checklistUrl}login#token=${token}`
  }

  reportsUrl() {
    let token = this.auth.token
    if (!isSomething(token)) {
      token = this.storage.get(KEY_TOKEN)
    }
    return `${environment.reportsUrl}login?token=${token}`
  }

  ownerCenterUrl() {
    let token = this.auth.token
    if (!isSomething(token)) {
      token = this.storage.get(KEY_TOKEN)
    }
    return `${environment.ownerCenterUrl}login?token=${token}`
  }

  onAIChat(isSubscribed: boolean) {
    if (!isSubscribed) {
      this.onSubscribeApp(ProductsForPlan.TokeetAI)
    } else {
      this.aiChatOverlay.open()
    }
  }

  onVisitAppStore() {
    this.amplitudeService.logEvent('visited-app-store')
  }

  onPushRates() {
    this.pushRatesOverlayService.openSide()
  }

  onAISettings() {
    this.aiSettings.open().subscribe()
  }

  onMessagingSettings() {
    this.messaginSettings.open()
  }

  onSubscribeApp(product: string) {
    this.appOverviewDialog.open(product)
  }

  onCostEstimator() {
    this.costEstimator.open()
  }
}
